/**
 * <featured-technologies> — two-level technology explorer (sections/featured-technologies.liquid).
 * CSS owns layout and motion (desktop list/panel split, mobile accordion, pill carousel,
 * grid-template-rows transitions keyed on data-open). This element only keeps the state
 * carriers in sync: data-open + aria-expanded (parent item / trigger), data-active (triggers,
 * pills, panels), inert (closed bodies) and the URL hash.
 *
 * State = openParent (parent handle | null) + active (parent or child handle | null).
 *   click closed parent → close the other, open it, active = parent
 *   click open parent   → active = parent; mobile only: collapse if it already was active
 *   click child         → active = child, parent stays open
 *   no / unknown hash   → desktop: first parent open + active; mobile: all collapsed
 * Only the mobile collapse rule and the defaults depend on the viewport (990px matchMedia).
 * The hash is written with history.replaceState — never pushState — so selections don't
 * pile up in history. Unknown hashes are ignored.
 *
 * The initial state is server-rendered as the desktop default; until data-ready is set the
 * stylesheet enforces the mobile default (collapsed) and disables transitions, so neither
 * viewport flashes or animates on load. data-ready is set one frame after init.
 *
 * Tracking: featured-technologies:select bubbles to document whenever the active item
 * changes through a click (never on re-click or on the mobile collapse) — the tracking
 * module listens to it because this element updates data-active before any document
 * click listener runs (see navigation-tracking.ts).
 *
 * Everything is block-scoped inside the customElements guard (slider.ts / quick-filters.ts
 * pattern): components compile as classic scripts sharing one global scope, so a top-level
 * `const REF` would collide with expandable-group.ts at build time.
 */
if (!customElements.get('featured-technologies')) {
    const REF = {
        item: '[data-ref="item"]',
        trigger: '[data-ref="trigger"]',
        body: '[data-ref="body"]',
        child: '[data-ref="child-trigger"]',
        panel: '[data-ref="panel"]',
    };
    const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
    const SELECT_EVENT = 'featured-technologies:select';
    /** Safety net for the transitionend-gated scroll (panel transition is 400ms). */
    const SCROLL_FALLBACK_MS = 600;
    class FeaturedTechnologies extends HTMLElement {
        parents = [];
        /** Every handle → its parent handle (a parent maps to itself). */
        parentOf = new Map();
        openParent = null;
        active = null;
        desktop = window.matchMedia(DESKTOP_MEDIA_QUERY);
        abort = null;
        /** Cleanup for a compensating scroll still waiting on transitionend — null when idle. */
        cancelPendingScroll = null;
        connectedCallback() {
            this.abort = new AbortController();
            const { signal } = this.abort;
            this.collect();
            this.addEventListener('click', this.handleClick, { signal });
            window.addEventListener('hashchange', this.handleHashChange, { signal });
            this.desktop.addEventListener('change', this.handleViewportChange, { signal });
            const target = this.resolveHash();
            if (target) {
                this.select(target);
                this.scrollToHashTarget('auto');
            }
            else {
                this.applyDefault();
            }
            // Next frame: the state above is painted without transitions first (stylesheet
            // disables them while data-ready is unset), then motion is enabled
            requestAnimationFrame(() => {
                if (this.isConnected)
                    this.dataset.ready = 'true';
            });
        }
        disconnectedCallback() {
            this.abort?.abort();
            this.abort = null;
            this.cancelPendingScroll?.();
        }
        // --- setup ----------------------------------------------------------------
        collect() {
            this.parents = [];
            this.parentOf.clear();
            this.querySelectorAll(REF.item).forEach((item) => {
                if (item.closest('featured-technologies') !== this)
                    return;
                const handle = item.dataset.handle;
                const trigger = item.querySelector(REF.trigger);
                if (!handle || !trigger)
                    return;
                const children = new Map();
                item.querySelectorAll(REF.child).forEach((child) => {
                    if (child.dataset.handle)
                        children.set(child.dataset.handle, child);
                });
                this.parents.push({ handle, item, trigger, body: item.querySelector(REF.body), children });
                this.parentOf.set(handle, handle);
                children.forEach((_, childHandle) => this.parentOf.set(childHandle, handle));
            });
        }
        findParent(handle) {
            return this.parents.find((parent) => parent.handle === handle);
        }
        // --- state ----------------------------------------------------------------
        /** Desktop: first parent open + active. Mobile: everything collapsed. Never touches the hash. */
        applyDefault() {
            const first = this.parents[0];
            if (this.desktop.matches && first) {
                this.openParent = first.handle;
                this.active = first.handle;
            }
            else {
                this.openParent = null;
                this.active = null;
            }
            this.render();
        }
        /** Open the parent of `handle` (closing any other) and make `handle` the active item. */
        select(handle) {
            const parent = this.parentOf.get(handle);
            if (!parent)
                return;
            this.openParent = parent;
            this.active = handle;
            this.render();
        }
        /** Mobile only: close the open parent, nothing active. */
        collapse() {
            this.openParent = null;
            this.active = null;
            this.render();
        }
        /** Push the state into the DOM. Everything the CSS keys on lives here. */
        render() {
            this.parents.forEach((parent) => {
                const open = parent.handle === this.openParent;
                parent.item.dataset.open = String(open);
                parent.trigger.setAttribute('aria-expanded', String(open));
                parent.trigger.dataset.active = String(parent.handle === this.active);
                // inert keeps pills, panel links and product cards of a closed parent out of the tab order
                if (parent.body)
                    parent.body.inert = !open;
                parent.children.forEach((child, childHandle) => {
                    child.dataset.active = String(childHandle === this.active);
                });
            });
            this.querySelectorAll(REF.panel).forEach((panel) => {
                panel.dataset.active = String(panel.dataset.handle === this.active);
            });
        }
        // --- events ---------------------------------------------------------------
        handleClick = (event) => {
            const target = event.target;
            if (!target)
                return;
            const child = target.closest(REF.child);
            if (child && this.contains(child)) {
                const handle = child.dataset.handle;
                if (!handle)
                    return;
                const changed = handle !== this.active;
                this.select(handle);
                this.writeHash(handle);
                if (changed)
                    this.emitSelect(child);
                return;
            }
            const trigger = target.closest(REF.trigger);
            if (!trigger || !this.contains(trigger))
                return;
            const handle = trigger.dataset.handle;
            if (!handle)
                return;
            const wasOpen = handle === this.openParent;
            if (wasOpen && handle === this.active && !this.desktop.matches) {
                this.collapse();
                this.writeHash(null);
                return;
            }
            const changed = handle !== this.active;
            this.select(handle);
            this.writeHash(handle);
            if (changed)
                this.emitSelect(trigger);
            if (!wasOpen && !this.desktop.matches)
                this.scrollAfterOpen(handle);
        };
        /** External hash navigation (in-page anchors, manual edits). replaceState never fires this. */
        handleHashChange = () => {
            const target = this.resolveHash();
            if (!target)
                return;
            this.select(target);
            this.scrollToHashTarget('smooth');
        };
        /** Entering desktop with nothing open would leave the right column empty. */
        handleViewportChange = () => {
            this.cancelPendingScroll?.();
            if (this.desktop.matches && this.openParent === null)
                this.applyDefault();
        };
        emitSelect(source) {
            this.dispatchEvent(new CustomEvent(SELECT_EVENT, {
                bubbles: true,
                detail: { handle: source.dataset.handle, name: source.dataset.trackingContentName },
            }));
        }
        // --- hash -----------------------------------------------------------------
        /** The current hash when it names a parent or child of this section, else null. */
        resolveHash() {
            const handle = window.location.hash.slice(1);
            return handle && this.parentOf.has(handle) ? handle : null;
        }
        writeHash(handle) {
            const { pathname, search } = window.location;
            window.history.replaceState(window.history.state, '', `${pathname}${search}${handle ? `#${handle}` : ''}`);
        }
        // --- scrolling ------------------------------------------------------------
        /** Hash landing: desktop scrolls to the section, mobile to the opened parent's title. */
        scrollToHashTarget(behavior) {
            if (this.desktop.matches) {
                this.scrollIntoView({ block: 'start', behavior });
                return;
            }
            const parent = this.openParent ? this.findParent(this.openParent) : undefined;
            parent?.trigger.scrollIntoView({ block: 'start', behavior });
        }
        /**
         * Mobile: once the opening body has finished its grid-template-rows transition (a parent
         * closing above shifts the page meanwhile), bring the clicked title to the top of the
         * viewport. The header offset is CSS scroll-margin-block-start on the trigger, not
         * computed here. Cancelled if the user scrolls during the wait. (expandable-group.ts)
         */
        scrollAfterOpen(handle) {
            this.cancelPendingScroll?.();
            const parent = this.findParent(handle);
            if (!parent)
                return;
            // Reduced motion: transitions are disabled, geometry is already final
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                parent.trigger.scrollIntoView({ block: 'start', behavior: 'auto' });
                return;
            }
            const scroll = () => {
                cleanup();
                parent.trigger.scrollIntoView({ block: 'start', behavior: 'smooth' });
            };
            const onTransitionEnd = (event) => {
                // The body also relays the icon's rotate transitionend — wait for the height one
                if (event.propertyName !== 'grid-template-rows')
                    return;
                scroll();
            };
            // The user scrolling during the wait means they took over — do not fight them
            const cancel = () => cleanup();
            // Safety net in case transitionend never fires (transition interrupted, viewport change)
            const timer = window.setTimeout(scroll, SCROLL_FALLBACK_MS);
            const cleanup = () => {
                window.clearTimeout(timer);
                parent.body?.removeEventListener('transitionend', onTransitionEnd);
                window.removeEventListener('wheel', cancel);
                window.removeEventListener('touchmove', cancel);
                this.cancelPendingScroll = null;
            };
            parent.body?.addEventListener('transitionend', onTransitionEnd);
            window.addEventListener('wheel', cancel, { passive: true });
            window.addEventListener('touchmove', cancel, { passive: true });
            this.cancelPendingScroll = cleanup;
        }
    }
    customElements.define('featured-technologies', FeaturedTechnologies);
}
